import { useEffect, useState } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { printHTML, downloadWord } from '../utils/printHelper';
import { formatDatePrint } from '../utils/dateFormat';
import { DateInput } from '../components/DateInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Plus, Pencil, Trash, Printer, FileDoc, UmbrellaSimple } from '@phosphor-icons/react';
import { confirmDialog } from '@/components/ConfirmDialog';
import ClientAutocomplete from '../components/ClientAutocomplete';

const EMPTY = {
  vrsta_zahtjeva: 'kupalista',
  client_id: '',
  datum_podnosenja: new Date().toISOString().split('T')[0],
  kup_naziv_podnosioca: '',
  kup_adresa_maticni: '',
  kup_vrsta_usluga: '',
  kup_mjesto: '',
  kup_period_duzina: '',
  kup_naziv_firme: '',
  kup_adresa_telefon: '',
  kup_godina: new Date().getFullYear(),
};

export default function KupalistaPage() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try {
      const [z, c] = await Promise.all([api.get('/zahtjevi?vrsta=kupalista'), api.get('/clients')]);
      setItems(z.data); setClients(c.data);
    } catch { toast.error('Greška'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const pickClient = (cid) => {
    const c = clients.find(x => x.id === cid);
    if (!c) return;
    setForm(p => ({
      ...p, client_id: cid,
      kup_naziv_podnosioca: c.naziv_firme || c.skraceni_naziv || '',
      kup_adresa_maticni: `${c.adresa || ''}, ${c.grad || ''} — MB (PIB): ${c.pib || ''}`.trim(),
      kup_naziv_firme: c.naziv_firme || c.skraceni_naziv || '',
      kup_adresa_telefon: `${c.adresa || ''}, ${c.grad || ''} — tel: ${c.telefon || ''}`.trim(),
    }));
  };

  const save = async () => {
    if (!form.kup_naziv_podnosioca) { toast.error('Unesite podnosioca'); return; }
    try {
      if (editingId) await api.put(`/zahtjevi/${editingId}`, form);
      else await api.post('/zahtjevi', form);
      toast.success('Sačuvano');
      setOpen(false); setEditingId(null); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Greška'); }
  };

  const edit = (z) => { setEditingId(z.id); setForm({ ...EMPTY, ...z }); setOpen(true); };
  const del = async (id) => {
    if (!(await confirmDialog('Obrisati zahtjev?'))) return;
    try { await api.delete(`/zahtjevi/${id}`); toast.success('Obrisano'); load(); } catch { toast.error('Greška'); }
  };

  const buildHtml = (z) => {
    const dayPart = z.datum_podnosenja ? String(new Date(z.datum_podnosenja).getDate()).padStart(2, '0') : '____';
    const monthPart = z.datum_podnosenja ? String(new Date(z.datum_podnosenja).getMonth() + 1).padStart(2, '0') : '____';
    const yearPart = z.kup_godina || (z.datum_podnosenja ? new Date(z.datum_podnosenja).getFullYear() : new Date().getFullYear());

    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Zahtjev kupališta</title>
<style>
@page { size: A4; margin: 10mm 14mm; }
* { margin:0; padding:0; box-sizing:border-box; font-family: Arial, sans-serif; }
body { font-family: Arial, sans-serif; font-size: 9pt; color: #000; line-height: 1.25; }
.header { text-align:left; margin-bottom: 4px; }
.header .cg { font-weight: bold; font-size: 10pt; }
.header .opstina { font-weight: bold; font-size: 9.5pt; }
.header-info { display: flex; justify-content: space-between; font-size: 8pt; margin-top: 2px; }
.header-info .left, .header-info .right { width: 48%; }
.header-info .right { text-align: right; }
.sep { border-top: 1.5px solid #000; margin: 5px 0; }
.dept { text-align:center; font-weight: bold; text-transform: uppercase; margin: 4px 0 6px; font-size: 9.5pt; }
.zahtjev-title { text-align:center; font-weight: bold; text-transform: uppercase; margin: 6px 0 2px; font-size: 10pt; }
.zahtjev-sub { text-align:center; font-weight: bold; text-transform: uppercase; margin-bottom: 6px; font-size: 9pt; line-height: 1.25; }
.alb { font-style: italic; font-weight: normal; color: #444; }
p { text-align: justify; margin: 3px 0; font-size: 8.5pt; }
.field { margin: 6px 0; }
.field-label { font-weight: bold; font-size: 8.5pt; }
.field-label .alb { font-weight: normal; }
.line { border-bottom: 1px solid #000; display: block; height: 16px; margin-top: 2px; padding: 1px 3px; }
.line.filled { border-bottom: 1px solid #000; height: auto; min-height: 16px; font-size: 8.5pt; }
.bottom-wrap { display: flex; justify-content: space-between; margin-top: 12px; gap: 14px; }
.bottom-left { width: 56%; }
.bottom-right { width: 42%; }
.date-loc { font-weight: bold; font-size: 9pt; }
.block { margin-bottom: 8px; }
.label { font-size: 7.5pt; font-weight: bold; line-height: 1.2; margin-top: 2px; }
.sig-line { border-bottom: 1px solid #000; min-height: 18px; padding-top: 3px; padding-left: 3px; font-size: 8.5pt; }
@media print { body { margin: 0; } }
</style></head><body>

<div class="header">
  <div class="cg">CRNA GORA / MALI I ZI</div>
  <div class="opstina">Opština Ulcinj / Komuna Ulqin</div>
  <div>Sekretarijat za privredu i ekonomski razvoj</div>
  <div class="alb">Sekretariati për ekonomi dhe zhvillim ekonomik</div>
</div>
<div class="header-info">
  <div class="left">Br./Nr.: 08-306<br>Ulcinj, Ulqin:</div>
  <div class="right">Adresa: Bul. Đ. Kastrioti Skenderbeg<br>85360 Ulcinj, Crna Gora<br>tel. 412-206<br>www.ul-gov.me</div>
</div>
<div class="sep"></div>

<div class="dept">SEKRETARIJAT ZA PRIVREDU I EKONOMSKI RAZVOJ<br><span class="alb">Sekretariati për ekonomi dhe zhvillim ekonomik</span></div>

<div class="zahtjev-title">ZAHTJEV / <span class="alb">KËRKESË</span></div>
<div class="zahtjev-sub">ZA IZDAVANJE ODOBRENJA ZA OBAVLJANJE DJELATNOSTI PRUŽANJE TURISTIČKIH USLUGA NA KUPALIŠTIMA<br>
<span class="alb" style="font-size:9.5pt;">Kërkesë për lëshimin e lejes për ushtrimin e aktivitetit ofrimi i shërbimeve turistike në plazhe</span></div>

<p>Obraćam se zahtjevom da mi izdate odobrenje za pružanje turističkih usluga na kupalištima za preduzetnika i privredno društvo: iznajmljivanje suncobrana, ležaljki, skutera na vodi, sandolina, pedalina, jedrilica, čamaca, dasaka za jedrenje, opreme za skijanje na vodi, ronilačke i ribolovne opreme, kao i usluge obuke skijanja na vodi, ronjenja, jedrenja i sl. <span style="font-style:italic;">(zaokružiti djelatnosti koji će se obavljati) (čl. 47 i 48)</span></p>
<p class="alb" style="font-size:9pt;">Kërkoj të më lëshoni lejen për ofrimin e shërbimeve turistike në plazhe për sipërmarrësin dhe shoqërinë ekonomike: dhënia e çadrave me qira, karrigeve për tu shtrirë, sandalleve, kajaks, anijeve të vogla me vela, varkave, surfboards, pajisjeve për ski në ujë, pajisjeve për zhytje dhe peshkim, si dhe shërbimeve të trajnimit për ski në ujë, zhytje, jahtin etj.</p>

<div class="field"><span class="field-label">1. Naziv i sjedište odnosno ime i prezime podnosioca zahtjeva: <span class="alb">/ Emri dhe adresa ose emri dhe mbiemri i parashtruesit të kërkesës:</span></span><div class="line filled">${z.kup_naziv_podnosioca || '&nbsp;'}</div></div>

<div class="field"><span class="field-label">2. Adresa i matični broj podnosioca zahtjeva: <span class="alb">/ Adresa dhe numri i amzës i parashtruesit të kërkesës:</span></span><div class="line filled">${z.kup_adresa_maticni || '&nbsp;'}</div></div>

<div class="field"><span class="field-label">3. Vrsta pružanja usluga: <span class="alb">/ Lloji i shërbimit të ofruar:</span></span><div class="line filled">${z.kup_vrsta_usluga || '&nbsp;'}</div></div>

<div class="field"><span class="field-label">4. Mjesto pružanja usluga: <span class="alb">/ Vendi i ofrimit të shërbimeve:</span></span><div class="line filled">${z.kup_mjesto || '&nbsp;'}</div></div>

<div class="field"><span class="field-label">5. Period pružanja usluga i dužina kupališta: <span class="alb">/ Periudha e ofrimit të shërbimeve dhe gjatësia e plazhit:</span></span><div class="line filled">${z.kup_period_duzina || '&nbsp;'}</div></div>

<div class="bottom-wrap">
  <div class="bottom-left">
    <div class="date-loc">U Ulcinju, ${dayPart}. ${monthPart}. ${yearPart} god.</div>
    <p style="font-weight:bold; margin-top: 10px; font-size: 9.5pt;">Uz zahtjev podnosim dokaze / <span class="alb" style="font-weight:normal;">Kërkesës i bashkangjes dëshmitë:</span></p>
    <ol style="padding-left: 16px; font-size: 7.5pt; line-height: 1.25; margin-top: 4px;">
      <li>Ovjerenu potvrdu o registraciji u Centralnom registru Privrednog Suda<br><span class="alb">Vërtetimin e vërtetuar të regjistrimit në Regjistrin qendror të Gjykatës ekonomike</span></li>
      <li>Fotokopija lične karte / <span class="alb">Fotokopjen e letërnjoftimit</span></li>
      <li>Rješenje o privremenoj lokaciji i ugovor o zakupu kupališta sa JP za upravljanje morskim dobrom (original)<br><span class="alb">Aktvendimin për lokacionin e përkohshëm dhe kontratën për marrjen me qira të plazhit</span></li>
      <li>Fotokopija polise osiguranja za slučaj nezgode na kupalištu<br><span class="alb">Fotokopjen e polisës së sigurimit në rast fatkeqësie në plazhe</span></li>
      <li>Zapisnik o ispunjavanju uslova u pogledu uređenosti i izgrađenosti kupališta<br><span class="alb">Procesverbalin për plotësimin e kushteve — Komuna e Ulqinit</span></li>
      <li>Uvjerenje o izmirenim obavezama poreza na nekretnine — kancelarija br. 117<br><span class="alb">Vërtetimin për obligimet e shlyera të tatimit në patundshmëri</span></li>
      <li>Zaključen ugovor o odvozu smeća na gradsku deponiju<br><span class="alb">Kontratën e lidhur për transportin e mbetjeve</span></li>
      <li>Popunjen obrazac za upis u Centralni turistički registar<br><span class="alb">Formulari i plotësuar për regjistrim në Regjistrin Qendror Turistik</span></li>
      <li>Dokaz o uplati admin. takse — <b>ŽR 540-82567770-12 Opština Ulcinj — 3,00 €</b></li>
      <li>Dokaz o uplati o ispunjavanju uslova — <b>ŽR 540-82567770-12</b>: do 50 m² <b>25€</b>, 50-200 m² <b>35€</b>, preko 200 m² <b>45€</b></li>
    </ol>
    <p style="font-weight:bold; margin-top:6px; font-size: 7.5pt;">SVE FOTOKOPIJE OVJERITI KOD NADLEŽNOG ORGANA<br><span class="alb" style="font-weight:normal;">TË GJITHA FOTOKOPJET DUHET TË VËRTETOHEN TE ORGANI KOMPETENT</span></p>
  </div>
  <div class="bottom-right">
    <div style="font-weight:bold; font-size:10.5pt; margin-bottom:8px;">
      PODNOSILAC ZAHTJEVA,<br>
      <span class="alb" style="font-weight:normal;">Parashtruesi i kërkesës,</span>
    </div>
    <div class="block">
      <div class="sig-line">${z.kup_naziv_firme || '&nbsp;'}</div>
      <div class="label">Naziv privrednog društva, drugog pravnog lica i preduzetnika<br><span class="alb" style="font-weight:normal;">Emri i shoq.ekonomike, personit tjetër juridike dhe sipërmarrësit</span></div>
    </div>
    <div class="block">
      <div class="sig-line">&nbsp;</div>
      <div class="label">Potpis odg. lica u priv. druš. i preduzetnika <span style="font-weight:normal; margin-left:10px;">M.P.</span><br><span class="alb" style="font-weight:normal;">Nënshkrimi i përsonit përgjegjës në shoq. ekonomike dhe sipërmarrësit</span></div>
    </div>
    <div class="block">
      <div class="sig-line">${z.kup_adresa_telefon || '&nbsp;'}</div>
      <div class="label">adresa i telefon / <span class="alb" style="font-weight:normal;">adresa dhe telefoni</span></div>
    </div>
  </div>
</div>

<script>window.onload=function(){window.print();window.onafterprint=function(){window.close()}}</script>
</body></html>`;
  };

  const print = (z) => printHTML(buildHtml(z));
  const word = (z) => downloadWord(buildHtml(z), `Kupalista_${(z.kup_naziv_podnosioca||'podnosilac').replace(/\s+/g,'_')}.doc`);

  return (
    <div className="space-y-6" data-testid="kupalista-page">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-slate-900">Kupališta — turističke usluge</h3>
          <p className="text-sm text-slate-600 mt-1">Zahtjev Opštini Ulcinj za izdavanje odobrenja za pružanje turističkih usluga na kupalištima</p>
        </div>
        <Button onClick={() => { setForm(EMPTY); setEditingId(null); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus size={20} weight="bold" className="mr-2" /> Novi zahtjev
        </Button>
      </div>

      {loading ? <p className="text-center text-slate-500 py-16">Učitavanje...</p> : items.length === 0 ? (
        <Card className="p-8 text-center">
          <UmbrellaSimple size={48} weight="duotone" className="text-slate-400 mx-auto mb-4" />
          <p className="text-slate-500">Nema zahtjeva za kupališta</p>
        </Card>
      ) : (
        <Card className="overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-left text-sm"><thead className="bg-slate-50 border-b"><tr>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Datum</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Podnosilac</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Mjesto</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Vrsta usluga</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Akcije</th>
        </tr></thead><tbody>{items.map(z => (
          <tr key={z.id} className="border-b hover:bg-slate-50">
            <td className="px-4 py-3 text-xs">{formatDatePrint(z.datum_podnosenja) || '-'}</td>
            <td className="px-4 py-3 font-medium">{z.kup_naziv_podnosioca || '-'}</td>
            <td className="px-4 py-3 text-xs">{z.kup_mjesto || '-'}</td>
            <td className="px-4 py-3 text-xs">{(z.kup_vrsta_usluga || '-').substring(0, 50)}</td>
            <td className="px-4 py-3"><div className="flex gap-1">
              <button onClick={() => print(z)} className="p-2 hover:bg-green-100 rounded" title="Štampaj"><Printer size={16} className="text-green-600" /></button>
              <button onClick={() => word(z)} className="p-2 hover:bg-indigo-100 rounded" title="Word dokument"><FileDoc size={16} className="text-indigo-600" /></button>
              <button onClick={() => edit(z)} className="p-2 hover:bg-slate-200 rounded" title="Uredi"><Pencil size={16} className="text-slate-600" /></button>
              <button onClick={() => del(z.id)} className="p-2 hover:bg-red-100 rounded" title="Obriši"><Trash size={16} className="text-red-600" /></button>
            </div></td>
          </tr>))}</tbody></table></div></Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? 'Uredi' : 'Novi zahtjev — Kupališta'}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <Card className="p-3 bg-blue-50 border-blue-200">
              <Label className="text-xs font-semibold uppercase text-blue-700 mb-2 block">Auto-popunjavanje iz klijenta</Label>
              <ClientAutocomplete clients={clients} value={form.client_id} onChange={(id) => (v => v !== 'none' && pickClient(v))(id || 'none')} placeholder="Pretraga po nazivu ili PIB-u..." />
            </Card>

            <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">1. Naziv/ime podnosioca *</Label>
              <Input value={form.kup_naziv_podnosioca} onChange={e => setForm({ ...form, kup_naziv_podnosioca: e.target.value })} className="bg-white border-slate-300 text-sm" /></div>
            <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">2. Adresa i matični broj</Label>
              <Input value={form.kup_adresa_maticni} onChange={e => setForm({ ...form, kup_adresa_maticni: e.target.value })} className="bg-white border-slate-300 text-sm" /></div>
            <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">3. Vrsta pružanja usluga</Label>
              <textarea value={form.kup_vrsta_usluga} onChange={e => setForm({ ...form, kup_vrsta_usluga: e.target.value })} placeholder="npr. iznajmljivanje suncobrana, ležaljki..." className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm min-h-[60px]" /></div>
            <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">4. Mjesto pružanja usluga</Label>
              <Input value={form.kup_mjesto} onChange={e => setForm({ ...form, kup_mjesto: e.target.value })} className="bg-white border-slate-300 text-sm" /></div>
            <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">5. Period i dužina kupališta</Label>
              <Input value={form.kup_period_duzina} onChange={e => setForm({ ...form, kup_period_duzina: e.target.value })} placeholder="npr. 01.05. — 01.10., dužina 100m" className="bg-white border-slate-300 text-sm" /></div>
            <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Datum podnošenja</Label>
              <DateInput value={form.datum_podnosenja} onChange={e => setForm({ ...form, datum_podnosenja: e.target.value })} className="border-slate-300 text-sm" /></div>

            <div className="flex gap-3 justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setOpen(false)}>Odustani</Button>
              <Button onClick={save} className="bg-blue-600 hover:bg-blue-700 text-white">{editingId ? 'Ažuriraj' : 'Kreiraj'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
